// ---------------------------------------------------------------------------
// DSS-14 shared: Primitive sub-components for the transform builder
// ---------------------------------------------------------------------------
import type React from "react";

// ── Utility ──
function safeNum(raw: number, fallback: number): number {
  const n = Number(raw);
  return Number.isNaN(n) ? fallback : n;
}

function clamp(val: number, min: number, max: number): number {
  return Math.min(Math.max(safeNum(val, min), min), max);
}

// ── Section ──
export function Section({ label, children }: { readonly label: string; readonly children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[10px] font-semibold text-[var(--color-foreground)]">{label}</span>
      {children}
    </div>
  );
}

// ── SliderRow ──
interface SliderRowProps {
  readonly id: string;
  readonly label: string;
  readonly min: number;
  readonly max: number;
  readonly value: number;
  readonly unit: string;
  readonly onChange: (v: number) => void;
}

export function SliderRow({ id, label, min, max, value: val, unit, onChange: onSlide }: SliderRowProps) {
  return (
    <div className="flex items-center gap-2">
      <label htmlFor={id} className="w-10 shrink-0 text-[10px] font-medium text-[var(--color-muted-foreground)]">
        {label}
      </label>
      <input
        id={id} type="range" min={min} max={max} step={1} value={val}
        onChange={(e) => onSlide(clamp(Number(e.target.value), min, max))}
        className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-[var(--color-border)] accent-[var(--color-primary)]"
      />
      <span className="w-12 text-right text-[10px] tabular-nums text-[var(--color-muted-foreground)]">
        {val}{unit}
      </span>
    </div>
  );
}

// ── UnitRow (translate with px/% toggle) ──
interface UnitRowProps {
  readonly uid: string;
  readonly axis: string;
  readonly value: number;
  readonly unit: "px" | "%";
  readonly onValue: (v: number) => void;
  readonly onUnit: (u: "px" | "%") => void;
}

export function UnitRow({ uid, axis, value: val, unit, onValue, onUnit }: UnitRowProps) {
  const id = `${uid}-t${axis.toLowerCase()}`;
  return (
    <div className="flex items-center gap-2">
      <label htmlFor={id} className="w-10 shrink-0 text-[10px] font-medium text-[var(--color-muted-foreground)]">
        {axis}
      </label>
      <input
        id={id} type="range" min={-500} max={500} step={1} value={val}
        onChange={(e) => onValue(safeNum(Number(e.target.value), 0))}
        className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-[var(--color-border)] accent-[var(--color-primary)]"
      />
      <input
        type="number" value={val} min={-500} max={500}
        onChange={(e) => onValue(clamp(Number(e.target.value), -500, 500))}
        className="h-6 w-14 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] px-1.5 text-right text-[10px] tabular-nums text-[var(--color-foreground)]"
      />
      <button
        type="button" onClick={() => onUnit(unit === "px" ? "%" : "px")}
        className="rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[9px] font-medium text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]"
      >
        {unit}
      </button>
    </div>
  );
}

// ── FlipButton ──
interface FlipButtonProps {
  readonly label: string;
  readonly isActive: boolean;
  readonly onClick: () => void;
}

export function FlipButton({ label, isActive, onClick }: FlipButtonProps) {
  return (
    <button
      type="button" onClick={onClick}
      className={[
        "rounded-md border px-3 py-1 text-[10px] font-medium",
        isActive
          ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
          : "border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]/50",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

// ── ToggleSwitch ──
interface ToggleSwitchProps {
  readonly isOn: boolean;
  readonly onToggle: () => void;
}

export function ToggleSwitch({ isOn, onToggle }: ToggleSwitchProps) {
  return (
    <button
      type="button" role="switch" aria-checked={isOn} onClick={onToggle}
      className={[
        "relative h-4 w-7 rounded-full border transition-colors",
        isOn
          ? "border-[var(--color-primary)] bg-[var(--color-primary)]"
          : "border-[var(--color-border)] bg-[var(--color-muted)]",
      ].join(" ")}
    >
      <span className={[
        "absolute top-0.5 h-2.5 w-2.5 rounded-full bg-white shadow-sm transition-transform",
        isOn ? "translate-x-3" : "translate-x-0.5",
      ].join(" ")} />
    </button>
  );
}
